import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Grid,
    Chip,
    InputAdornment
} from '@mui/material';
import { Search, Add, Folder, Business } from '@mui/icons-material';
import { projectsApi, Project } from '../services/projectsApi';
import { customersApi } from '../services/customersApi';
import GenericPage from '../../Commun/components/GenericPage';
import { ProjectCreateDialog } from '../components/ProjectCreateDialog';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import './Projects.css';

const Projects: React.FC = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [customers, setCustomers] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    useEffect(() => {
        loadProjects();
        loadCustomers();
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const response = await projectsApi.getAll();
            setProjects(response.data || []);
        } catch (error) {
            console.error('Error loading projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCustomers = async () => {
        try {
            // Assuming customersApi is/will be migrated similarly
            const response = await customersApi.getAll();
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    const filteredProjects = projects.filter(project => {
        const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'All' ||
            (statusFilter === 'Active' && project.isActive) ||
            (statusFilter === 'Inactive' && !project.isActive);
        return matchesSearch && matchesStatus;
    });

    return (
        <GenericPage
            title={t('maker.projects.title')}
            icon={<Business />}
            footerEnabled={false}
            additionalActions={
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setIsModalOpen(true)}
                >
                    {t('maker.projects.new')}
                </Button>
            }
        >
            <Box className="projects-content" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box className="projects-filters" sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                        placeholder={t('maker.projects.search')}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flexGrow: 1, maxWidth: 400 }}
                    />
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="All">{t('maker.projects.filter.status')}</MenuItem>
                            <MenuItem value="Active">{t('maker.projects.filter.active')}</MenuItem>
                            <MenuItem value="Inactive">{t('maker.projects.filter.inactive')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box className="projects-list" sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            {t('maker.projects.loading')}
                        </Box>
                    ) : filteredProjects.length === 0 ? (
                        <Box className="empty-state-card" sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2 }}>
                            <Folder sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">{t('maker.projects.empty.title')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('maker.projects.empty.desc')}
                            </Typography>
                            <Button variant="outlined" onClick={() => setIsModalOpen(true)}>
                                {t('maker.projects.create')}
                            </Button>
                        </Box>
                    ) : (
                        <Grid container spacing={3} className="projects-grid">
                            {filteredProjects.map((project) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
                                    <Card
                                        className="project-card"
                                        onClick={() => navigate('/Maker/design', { state: { projectId: project.id } })}
                                        sx={{
                                            cursor: 'pointer',
                                            height: '100%',
                                            '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', transition: 'all 0.2s' }
                                        }}
                                    >
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom noWrap title={project.name}>
                                                {project.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{
                                                mb: 2,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                height: '40px'
                                            }}>
                                                {project.description || t('maker.projects.card.noDesc')}
                                            </Typography>
                                            <Box className="project-meta" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('maker.projects.meta.created')} {new Date(project.createdDate).toLocaleDateString()}
                                                </Typography>
                                                <Chip
                                                    label={project.isActive ? t('maker.projects.filter.active') : t('maker.projects.filter.inactive')}
                                                    size="small"
                                                    color={project.isActive ? 'success' : 'default'}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Box>

            <ProjectCreateDialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={loadProjects}
                customers={customers}
            />
        </GenericPage>
    );
}

export default Projects;

